import 'core-js/stable';
import 'regenerator-runtime/runtime';
import React from 'react';
import PropTypes from 'prop-types';
import { observer, inject, Provider } from 'mobx-react';

import { analytics } from 'core/analytics';

// i18n
import { I18nextProvider } from 'react-i18next';
import { i18n } from 'config/i18n';
// views
import { configure_bloqi } from 'coreConf';
import { ClientContext } from 'apps/client/index';
// Global app styles
import { ThemeProvider } from 'styled-components';
import globalStyles from 'styles/globalStyles';
import GlobalStylesMobile from 'styles/globalStylesMobile';
import theme from 'styles/themes/wikifactory';
// Chakra
import { ChakraProvider, ColorModeScript } from '@chakra-ui/react';
import chakraTheme from 'styles/themes/wikifactory/chakra';
import { Global } from '@emotion/react';

// routing
import { routeNode } from 'core/routing';

// Context providers
import { RootStoresProvider } from 'apps/hooks';

// whitelabel
import { whitelabelPatchTheme } from 'core/whitelabel';

// components
import DocumentHead from 'legacyComponents/documentHead';
import { ProgressBar } from 'legacyComponents/progressBar';
import { PolicyMessage } from 'apps/components/policyMessage';
import { AlertCenter } from 'components';

// apps
import { FourOhFour } from 'apps/fourOhFour/index';
import { MessageBanner } from 'legacyComponents/messageBanner';
import { ModalContainer } from 'apps/modals';
import { ProcessMonitor } from 'apps/processMonitor';
import Footer from 'apps/components/footer/footer';

// top-level stores
import { rootStore } from 'apps/store';

// routers
import { HomeRouter } from 'apps/home/router';
import { ProfileRouter } from 'apps/profile/router';
import { InitiativeRouter } from 'apps/initiative/router';
import WorkspaceRouter from 'apps/workspace/router';

import { ChannelRouter } from 'apps/channel/router';
// import { ManufacturingRouter } from 'apps/manufacturing/router';
import SharePage from 'apps/share/sharePage';

import MakePartRouter from 'apps/makePart/router';
import RFQPage from 'apps/rfq/rfqPage';
import RFQsPage from 'apps/rfq/rfqList/page';
// import OrderPage from 'apps/order/orderPage';
import OrdersPage from 'apps/order/orderList/page';
import { ViewerPage } from 'apps/viewer';
import { initYellowBanner } from 'apps/components/header/blocks/yellowBanner';
import { conf as globalConf } from 'apps/config.yaml';

import { ChatControlProvider } from 'libs/rendering/hooks';

import { ViewerControlProvider } from 'libs/rendering/gltf/hooks';

import { ChatControl } from 'libs/rendering/chatControl';
import { ViewerControl } from 'libs/rendering/gltf/viewerControl';
import { Canvas3D } from 'libs/rendering/gltf/components/canvas3D';

import TagManager from 'react-gtm-module';
import FrameMobile from 'apps/components/FrameMobile';
import Frame from 'apps/components/Frame';
import SpacingToAvoidFrame from 'apps/components/Frame/SpacingToAvoidFrame';

import { frameStore } from 'apps/components/Frame/appManager';

import { CreateShipmentQuotePage } from './shipmentQuote/CreateShipmentQuotePage';
import { QuotePage } from './shipmentQuote/QuotePage';
import { OrderPage } from './order/OrderPage';

import TestPage from '../../tests/sandbox';

// patch fetch to return a Promise with 'finally'
window._fetch = fetch;
if (!window._fetch) {
  window.fetch = (...args) => Promise.resolve(window._fetch(...args));
}

// polyfill canvas.toBlob
if (!HTMLCanvasElement.prototype.toBlob) {
  Object.defineProperty(HTMLCanvasElement.prototype, 'toBlob', {
    value(callback, type, quality) {
      const canvas = this;
      setTimeout(() => {
        const binStr = atob(canvas.toDataURL(type, quality).split(',')[1]);
        const len = binStr.length;
        const arr = new Uint8Array(len);

        for (let i = 0; i < len; i++) {
          arr[i] = binStr.charCodeAt(i);
        }

        callback(new Blob([arr], { type: type || 'image/png' }));
      });
    },
  });
}

configure_bloqi();
initYellowBanner();

const tagManagerArgs = {
  gtmId: GOOGLE_TAGS_ID,
};

const components = {
  home: HomeRouter,
  // dashboard: HomeRouter,
  profile: ProfileRouter,
  initiative: InitiativeRouter,
  channel: ChannelRouter,

  // manufacturing: ManufacturingRouter,
  'share-file': SharePage,
  'make-part': MakePartRouter,
  rfq: RFQPage,
  rfqs: RFQsPage,
  quote: QuotePage,
  order: OrderPage,
  orders: OrdersPage,
  viewer: ViewerPage,

  workspace: WorkspaceRouter,
  'new-quote': CreateShipmentQuotePage,
  // /posts/id" PostPage
  test: TestPage,
};

const routesWithoutFrame = [
  'home.signIn',
  'home.signUp',
  'home.newProject',
  'home.newInitiative',
  'viewer',
];

@inject('routerStore', 'clientStore', 'headerStore')
@observer
class App extends React.Component {
  constructor(props) {
    super(props);
    props.clientStore.initialize();
    frameStore.initialize();
    analytics.initialize(props.clientStore.signedInProfile);
  }

  componentDidMount() {
    const { routerStore, headerStore } = this.props;

    if (GOOGLE_TAGS_ID) {
      TagManager.initialize(tagManagerArgs);
    }

    // Add Hubspot script
    if (window.location.hostname === 'wikifactory.com') {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.id = 'hs-script-loader';
      script.async = true;
      script.defer = true;
      script.src = '//js-eu1.hs-scripts.com/26723949.js';
      document.body.appendChild(script);
    }

    document.addEventListener('touchstart', () => {}, true);

    // hide mobile nav on navigate success.
    const { router } = routerStore;
    const hideMobileNav = () => ({
      onTransitionSuccess: () => {
        if (headerStore.hamburgerActive) {
          headerStore.ToggleHamburger();
        }
      },
    });
    router.usePlugin(hideMobileNav);
  }

  render() {
    const { route } = this.props.routerStore;
    if (!route) return null;
    const { clientStore } = this.props;
    const { hamburgerActive } = this.props.headerStore;
    let segment;
    if (['INTERMEDIATOR', 'MANUFACTURER'].includes(clientStore.currentPortal)) {
      if (route.name.startsWith('workspace')) {
        // delegate handling of intermediator to WorkspaceRouter
        segment = 'workspace';
      } else {
        [, segment] = route.name.split('.');
      }
    } else {
      [segment] = route.name.split('.');
    }

    const View = components[segment] || FourOhFour;
    const title = globalConf.siteTitle;
    const description = globalConf.siteDescription;

    const isLoggedIn = !!clientStore.signedInProfile;
    const shouldShowFrame =
      isLoggedIn && !routesWithoutFrame.includes(route.name);

    const isMobileViewport = clientStore.isMobile;

    const mainContent = (
      <>
        <ProgressBar />
        <MessageBanner />
        <ModalContainer />
        <View />
        <AlertCenter />
        {// - in the preview generation I used to manually delete the
        // PolicyMessage(cookie banner), but that was unreliable so instead
        // I prevent it from appearing here
        segment !== 'viewer' && <PolicyMessage />}
        <ProcessMonitor />
      </>
    );

    return (
      <>
        {hamburgerActive && <GlobalStylesMobile />}
        <DocumentHead title={title} description={description} />
        <main>
          {shouldShowFrame && !isMobileViewport && <Frame />}
          {shouldShowFrame && isMobileViewport && <FrameMobile />}
          <SpacingToAvoidFrame active={shouldShowFrame}>
            {mainContent}
          </SpacingToAvoidFrame>
        </main>
        {!isLoggedIn && <Footer />}
      </>
    );
  }
}

@observer
export class Wikifactory extends React.Component {
  constructor(props) {
    super(props);
    this.chatControl = new ChatControl();
    this.viewerControl = new ViewerControl();
  }

  render() {
    const patchedTheme = whitelabelPatchTheme(theme);
    // TODO: remove 'Provider' when we've removed all mobx injects
    return (
      <I18nextProvider i18n={i18n}>
        <ClientContext>
          <ThemeProvider theme={patchedTheme}>
            <ChakraProvider theme={chakraTheme}>
              <Global styles={globalStyles} />
              <ColorModeScript
                initialColorMode={chakraTheme?.config?.initialColorMode}
              />
              <RootStoresProvider value={rootStore}>
                <Provider rootStore={rootStore} {...rootStore.stores}>
                  <ChatControlProvider value={this.chatControl}>
                    <ViewerControlProvider value={this.viewerControl}>
                      <RouteApp />
                      <Canvas3D />
                    </ViewerControlProvider>
                  </ChatControlProvider>
                </Provider>
              </RootStoresProvider>
            </ChakraProvider>
          </ThemeProvider>
        </ClientContext>
      </I18nextProvider>
    );
  }
}

export const RouteApp = routeNode('')(App);

App.propTypes = {
  routerStore: PropTypes.object,
  clientStore: PropTypes.object,
  headerStore: PropTypes.object,
};
